// Keyboard shortcut registry — pure core + thin DOM hook.
//
// Supported patterns:
//   Single keys : `c`, `/`, `?`
//   g-chord     : one per NavTarget with a `hotkey` in lib/navigation.ts
//                 (press `g`, then within CHORD_TIMEOUT_MS press second key)
//                 — the second-key -> target map is `hotkeyMap()` from
//                 lib/navigation.ts, the single source of truth for
//                 navigation (see that file's header comment). This module
//                 no longer hand-maintains its own chord catalog.
//
// Events are ignored when:
//   - focus is inside an INPUT, TEXTAREA, SELECT, or contenteditable element
//   - a meta, ctrl, or alt modifier is held (Cmd+K is handled separately in App.tsx)

import { useEffect } from "preact/hooks";
import { hotkeyMap } from "./navigation";

// ---------------------------------------------------------------------------
// Action IDs
// ---------------------------------------------------------------------------
//
// Single-key actions resolve to the fixed literals below. g-chord actions
// resolve to `nav:${path}` — the path comes straight from the matched
// NavTarget, so there is no second hand-maintained action-id -> path table.
export type HotkeyAction = "create" | "capture" | "search" | "help" | `nav:${string}`;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when the given element is an editable target where typing
 * should not trigger hotkeys.
 */
export function isEditableTarget(el: Element | null | undefined): boolean {
  if (!el) return false;
  const tag = (el as HTMLElement).tagName?.toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  // Check both the reflected property and the attribute directly, because some
  // test environments (happy-dom) do not compute isContentEditable correctly.
  const ce = (el as HTMLElement).getAttribute("contenteditable");
  if ((el as HTMLElement).isContentEditable || ce === "true" || ce === "") return true;
  return false;
}

// ---------------------------------------------------------------------------
// HotkeyMatcher — testable pure state machine
// ---------------------------------------------------------------------------

export interface HotkeyMatcherOptions {
  /** Called when an action resolves. */
  onAction: (action: HotkeyAction) => void;
  /** Chord timeout in ms. Defaults to 1000. */
  chordTimeoutMs?: number;
  /** Pluggable timestamp supplier (ms). Defaults to Date.now. */
  now?: () => number;
  /**
   * Second chord key -> resolved action. Defaults to `buildChordMap()`
   * (derived from lib/navigation.ts `hotkeyMap()`). Overridable for tests.
   */
  chordMap?: Record<string, HotkeyAction>;
}

export interface KeyEvent {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  target: Element | null | undefined;
}

/**
 * Builds the g-chord second-key -> action map from the single navigation
 * source (lib/navigation.ts `hotkeyMap()`), instead of hand-maintaining a
 * second catalog here. Includes both each target's primary hotkey and its
 * `hotkeyAliases` (e.g. the old `g m` chord keeps working as an alias of
 * `g a` for "Aufgaben").
 */
export function buildChordMap(): Record<string, HotkeyAction> {
  const map: Record<string, HotkeyAction> = {};
  for (const [key, target] of Object.entries(hotkeyMap())) {
    map[key] = `nav:${target.path}`;
  }
  return map;
}

/** Direct single-key map (no modifier). */
const SINGLE_KEYS: Record<string, HotkeyAction> = {
  c: "create",
  // UX-Vorschlag P2: globale Schnellerfassung. Der Handler in App.tsx prüft
  // das Flag `ux_web_p2` — ohne Flag bleibt N wie bisher wirkungslos.
  n: "capture",
  "/": "search",
  "?": "help",
};

export interface HotkeyMatcher {
  /**
   * Feed a key event into the matcher. Returns the resolved action (or null
   * if no action was triggered). Also calls onAction if one resolves.
   */
  feed(event: KeyEvent): HotkeyAction | null;
  /** Current pending chord state (exposed for testing). */
  readonly pendingChord: boolean;
}

/**
 * Creates a stateful but pure matcher. The only side-effect is calling
 * `onAction`; all timing is injectable via `now()`.
 */
export function createHotkeyMatcher(options: HotkeyMatcherOptions): HotkeyMatcher {
  const { onAction, chordTimeoutMs = 1000, now = Date.now, chordMap = buildChordMap() } = options;

  let pendingG = false;
  let pendingGTimestamp = 0;

  function resolve(action: HotkeyAction): HotkeyAction {
    onAction(action);
    return action;
  }

  return {
    get pendingChord(): boolean {
      return pendingG;
    },
    feed(event: KeyEvent): HotkeyAction | null {
      // Ignore events from editable targets.
      if (isEditableTarget(event.target)) return null;
      // Ignore events with meta/ctrl/alt modifiers.
      if (event.metaKey || event.ctrlKey || event.altKey) return null;

      const k = event.key;

      // --- Chord resolution ---
      if (pendingG) {
        const elapsed = now() - pendingGTimestamp;
        if (elapsed > chordTimeoutMs) {
          // Chord timed out — reset and fall through to process current key.
          pendingG = false;
        } else {
          // We are in an active chord.
          pendingG = false;
          const chordAction = chordMap[k];
          if (chordAction) return resolve(chordAction);
          // Unmapped second key — chord aborts, no action.
          return null;
        }
      }

      // --- Single key: start chord on `g` ---
      if (k === "g") {
        pendingG = true;
        pendingGTimestamp = now();
        return null;
      }

      // --- Single key actions ---
      const singleAction = SINGLE_KEYS[k];
      if (singleAction) return resolve(singleAction);

      return null;
    },
  };
}

// ---------------------------------------------------------------------------
// DOM hook — thin wrapper that binds window keydown
// ---------------------------------------------------------------------------

export interface UseHotkeysHandlers {
  onCreate?: () => void;
  onCapture?: () => void;
  onSearch?: () => void;
  onHelp?: () => void;
  onNavigate?: (path: string) => void;
}

/**
 * Binds global keydown hotkeys for the duration of the component's life.
 * Must be called from within a Preact component (uses useEffect).
 */
export function useHotkeys(handlers: UseHotkeysHandlers): void {
  useEffect(() => {
    const matcher = createHotkeyMatcher({
      onAction(action) {
        switch (action) {
          case "create":
            handlers.onCreate?.();
            break;
          case "capture":
            handlers.onCapture?.();
            break;
          case "search":
            handlers.onSearch?.();
            break;
          case "help":
            handlers.onHelp?.();
            break;
          default:
            if (action.startsWith("nav:")) {
              handlers.onNavigate?.(action.slice(4));
            }
        }
      },
    });

    const onKeyDown = (e: KeyboardEvent): void => {
      matcher.feed({
        key: e.key,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        altKey: e.altKey,
        target: (e.target as Element | null),
      });
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // Handlers object changes on every render; we intentionally do NOT
    // re-subscribe — callers should use stable callbacks or signals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
